import { JSX, ReactNode } from 'react';

import { useField } from 'react-final-form';

import { FileInput as Input, FileInputValue } from '@vk-hr-tek/ui/input';

import { composeValidators, ValidateType } from '../validation';

/**
 * Свойства компонента {@link FileInput}
 */
type FileInputProps = {
  /**
   * Подпись поля.
   */
  label: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Всплывающая подсказка.
   */
  tooltip?: ReactNode;

  /**
   * Имя поля.
   */
  name: string;

  /**
   * Вариант отображения поля.
   *
   * @defaultValue `'input'`
   */
  variant?: 'input' | 'preview';

  /**
   * Текст заполнителя.
   */
  placeholder?: string;

  /**
   * Обязательность поля.
   *
   * @defaultValue `false`
   */
  required?: boolean;

  /**
   * Недоступность поля.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Обработчик изменения значения.
   */
  onChange?: (value?: FileInputValue) => void;

  /**
   * Состояние загрузки.
   *
   * @defaultValue `false`
   */
  loading?: boolean;

  /**
   * Текст при загрузке.
   */
  loadingText?: JSX.Element;

  /**
   * Показывать ошибку без взаимодействия с полем.
   */
  showErrorWithoutTouched?: boolean;

  /**
   * Массив валидаторов.
   */
  validate?: ValidateType[];

  /**
   * Функция для кэширования файла.
   */
  cache?: (file: File) => Promise<{ file_id: string; expired_at: string }>;

  /**
   * Занимать всю ширину контейнера.
   */
  fullWidth?: boolean;

  /**
   * Текст баннера.
   */
  bannerText?: string;

  /**
   * Флаг распознавания QR-кода.
   */
  autorecognitionQr?: boolean;

  /**
   * Дополнительный текст помощи.
   */
  helperText?: string;

  /**
   * Флаг ошибки.
   */
  error?: boolean;

  /**
   * Текст ошибки.
   */
  errorText?: string;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;

  /**
   * Флаг для масштабирования документа.
   *
   * @defaultValue `false`
   */
  withZoom?: boolean;

  /**
   * Флаг авторизации.
   */
  withAuth?: boolean;

  /**
   * Показывать ошибку без взаимодействия с полем.
   *
   * @defaultValue `false`
   */
  validationWithoutTouch?: boolean;

  /**
   * Показывать ошибку если поле задизейблено.
   *
   * @defaultValue `false`
   */
  validationWhenDisabled?: boolean;
};

/**
 * Компонент для загрузки файла с возможностью предварительного просмотра с интеграцией в react-final-form.
 *
 * @public
 * @param label - Подпись поля.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Всплывающая подсказка.
 * @param name - Имя поля.
 * @param variant - Вариант отображения поля.
 * @param placeholder - Текст заполнителя.
 * @param required - Обязательность поля.
 * @param disabled - Недоступность поля.
 * @param onChange - Обработчик изменения значения.
 * @param loading - Состояние загрузки.
 * @param loadingText - Текст при загрузке.
 * @param validate - Массив валидаторов.
 * @param cache - Функция для кэширования файла.
 * @param fullWidth - Занимать всю ширину контейнера.
 * @param bannerText - Текст баннера.
 * @param autorecognitionQr - Флаг распознавания QR-кода.
 * @param helperText - Дополнительный текст помощи.
 * @param error - Флаг ошибки.
 * @param errorText - Текст ошибки.
 * @param testId - Идентификатор для тестирования.
 * @param withAuth - Флаг авторизации.
 * @param withZoom - Флаг для масштабирования документа.
 * @param validationWithoutTouch - Показывать ошибку без взаимодействия с полем.
 * @param validationWhenDisabled - Показывать ошибку если поле задизейблено.
 * @returns Компонент для загрузки файла.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link FileInputProps}
 */
export const FileInput = ({
  label,
  labelIcon,
  tooltip,
  name,
  variant = 'input',
  placeholder,
  required = false,
  disabled = false,
  onChange,
  loading = false,
  loadingText = undefined,
  validate,
  cache,
  fullWidth,
  bannerText,
  autorecognitionQr = false,
  helperText,
  error: errorOverride,
  errorText: errorTextOverride,
  testId,
  withAuth,
  withZoom = false,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
}: FileInputProps) => {
  const validators = composeValidators(validate || []);
  const { input, meta } = useField(name, {
    validate: validators,
  });

  const { onChange: onChangeInput, onBlur } = input;

  const errorDisabled = disabled || !validationWhenDisabled;

  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && meta.error;

  return (
    <Input
      {...input}
      label={label}
      labelIcon={labelIcon}
      value={input.value}
      onChange={(props) => {
        onChangeInput(props);
        onChange?.(props);
      }}
      variant={variant}
      tooltip={tooltip}
      required={required}
      error={errorOverride || renderError}
      errorText={errorTextOverride || (renderError && meta.error)}
      helperText={helperText}
      disabled={disabled}
      loading={loading}
      loadingText={loadingText}
      onBlur={onBlur}
      cache={cache}
      fullWidth={fullWidth}
      placeholder={placeholder}
      autorecognitionQr={autorecognitionQr}
      bannerText={bannerText}
      testId={testId}
      withZoom={withZoom}
      validators={validators}
      withAuth={withAuth}
    />
  );
};
